import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import FloatingFAQ from '@/components/FloatingFAQ';
import StickyReservationCTA from '@/components/StickyReservationCTA';
import { ScrollReveal } from '@/components/ScrollReveal';
import { 
  MapPin, 
  Clock, 
  Route, 
  Users, 
  Mountain,
  Waves,
  TreePine,
  Bike,
  ArrowRight,
  Filter,
  ExternalLink,
  Calendar,
  Compass,
  CheckCircle,
  Smartphone
} from 'lucide-react';

const Balades = () => {
  const [activeSecteur, setActiveSecteur] = useState('tous');

  const secteurs = [
    { id: 'tous', label: 'Tous les secteurs', icon: Route },
    { id: 'ile-de-re', label: 'Île de Ré', icon: MapPin },
    { id: 'montalivet', label: 'Montalivet & Médoc', icon: MapPin },
    { id: 'landes', label: 'Landes', icon: TreePine },
    { id: 'charente-maritime', label: 'Charente-Maritime', icon: Waves },
    { id: 'marais-poitevin', label: 'Marais Poitevin', icon: Waves }
  ];

  const circuits = [
    // ÎLE DE RÉ
    {
      id: 'tour-ile-re',
      title: 'Tour de l\'Île de Ré',
      secteur: 'ile-de-re',
      description: 'Le grand classique ! Pistes cyclables sécurisées à travers villages typiques et marais salants. Parfait pour découvrir l\'île en famille.',
      ambiance: 'Paysages côtiers, villages authentiques, patrimoine maritime',
      image: './images/ile_de_re_cycling_20260110_101615.png',
      distance: '42 km',
      niveau: 'Facile',
      duree: '6-8h',
      velo: 'Vélo de ville ou électrique',
      highlights: ['Phare des Baleines', 'Villages fortifiés', 'Marais salants']
    },
    {
      id: 'villages-ile-re',
      title: 'Villages & Ports de Charme',
      secteur: 'ile-de-re',
      description: 'Circuit découverte à travers les plus beaux villages de l\'île. Petites routes tranquilles entre patrimoine et authenticité.',
      ambiance: 'Villages de caractère, ports de pêche, architecture locale',
      image: './images/quality_assurance_bikes_20260110_101614.png',
      distance: '28 km',
      niveau: 'Facile',
      duree: '4-5h',
      velo: 'Vélo de ville',
      highlights: ['Ars-en-Ré', 'La Flotte', 'Saint-Martin-de-Ré']
    },
    
    // MONTALIVET & MÉDOC
    {
      id: 'plages-montalivet',
      title: 'Circuit des Plages Océanes',
      secteur: 'montalivet',
      description: 'Entre océan Atlantique et forêt de pins, un parcours ressourçant sur pistes forestières face aux vagues.',
      ambiance: 'Océan sauvage, forêt de pins, dunes naturelles',
      image: './images/montalivet_beach_cycling_20260110_101615.png',
      distance: '25 km',
      niveau: 'Facile',
      duree: '3-4h',
      velo: 'VTT ou vélo tout-chemin',
      highlights: ['Plage de Montalivet', 'Forêt des Landes', 'Dunes sauvages']
    },
    {
      id: 'vignobles-medoc',
      title: 'Route des Châteaux Viticoles',
      secteur: 'montalivet',
      description: 'Découverte des prestigieux vignobles médocains à travers petites routes de campagne et paysages viticoles.',
      ambiance: 'Vignobles renommés, châteaux historiques, terroir d\'exception',
      image: './images/professional_services_hero_20260110_101615_20260113_161851.png',
      distance: '35 km',
      niveau: 'Intermédiaire',
      duree: '5-6h',
      velo: 'Vélo électrique recommandé',
      highlights: ['Châteaux viticoles', 'Paysages de vignes', 'Villages médocains']
    },
    
    // LANDES
    {
      id: 'foret-landes',
      title: 'Échappée dans la Grande Forêt',
      secteur: 'landes',
      description: 'Immersion dans la plus grande forêt artificielle d\'Europe. Sentiers ombragés et lacs cachés pour une balade nature.',
      ambiance: 'Forêt de pins, sentiers ombragés, lacs naturels',
      image: './images/bike_testing_service_20260110_101615.png',
      distance: '30 km',
      niveau: 'Intermédiaire',
      duree: '4-5h',
      velo: 'VTT ou vélo électrique',
      highlights: ['Sentiers forestiers', 'Lacs cachés', 'Faune sauvage']
    },
    
    // CHARENTE-MARITIME
    {
      id: 'cote-charentaise',
      title: 'Côte Sauvage Charentaise',
      secteur: 'charente-maritime',
      description: 'Parcours sportif le long de la côte sauvage avec panoramas exceptionnels sur l\'océan Atlantique.',
      ambiance: 'Côte sauvage, falaises, panoramas océan',
      image: './images/professional_services_hero_20260110_101615_20260113_161851.png',
      distance: '45 km',
      niveau: 'Sportif',
      duree: '6-7h',
      velo: 'Vélo de route ou gravel',
      highlights: ['Falaises spectaculaires', 'Panoramas océan', 'Phares historiques']
    },
    
    // MARAIS POITEVIN
    {
      id: 'marais-poitevin',
      title: 'Venise Verte à Vélo',
      secteur: 'marais-poitevin',
      description: 'Exploration paisible des canaux du Marais Poitevin. Chemins tranquilles le long des voies d\'eau dans un cadre préservé.',
      ambiance: 'Canaux verdoyants, nature préservée, tranquillité',
      image: './images/editorial_field_stories_illustration_20260109_172240.png',
      distance: '22 km',
      niveau: 'Très facile',
      duree: '3-4h',
      velo: 'Vélo de ville',
      highlights: ['Canaux verdoyants', 'Faune locale', 'Villages authentiques']
    }
  ];

  const circuitsFiltres = circuits.filter(circuit => {
    return activeSecteur === 'tous' || circuit.secteur === activeSecteur;
  });

  const getNiveauColor = (niveau: string) => {
    switch (niveau) {
      case 'Très facile': return 'bg-green-100 text-green-800';
      case 'Facile': return 'bg-green-100 text-green-800';
      case 'Intermédiaire': return 'bg-yellow-100 text-yellow-800';
      case 'Sportif': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section Unique avec Photo */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Image de fond */}
        <div className="absolute inset-0">
          <img 
            src="./images/ile_de_re_cycling_20260110_101615.png" 
            alt="Cyclistes sur l'Île de Ré" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/60"></div>
        </div>
        
        {/* Contenu */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <ScrollReveal>
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Compass className="h-8 w-8" />
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Besoin d'inspiration pour vos balades ?
            </h1>
            <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto mb-10">
              Découvrez nos circuits testés et recommandés par secteur : Île de Ré, Montalivet, Landes…
            </p>
            <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 font-semibold text-lg px-8 py-4 rounded-xl">
              <a href="#circuits">
                Découvrir les balades & circuits
                <ArrowRight className="ml-2 h-6 w-6" />
              </a>
            </Button>
          </ScrollReveal>
        </div>
      </section>

      {/* Section Principale : Balades & Circuits par secteur */}
      <section className="py-20" id="circuits">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Balades & Circuits par secteur
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                Des parcours sélectionnés et testés par notre équipe pour vous faire découvrir les plus beaux paysages de la côte atlantique.
              </p>
            </div>
          </ScrollReveal>

          {/* Filtres par secteur */}
          <ScrollReveal delay={100}>
            <div className="mb-12">
              <div className="flex items-center justify-center gap-4 mb-6">
                <Filter className="h-5 w-5 text-slate-600" />
                <span className="font-medium text-slate-900">Filtrer par secteur :</span>
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                {secteurs.map((secteur) => {
                  const IconComponent = secteur.icon;
                  return (
                    <button
                      key={secteur.id}
                      onClick={() => setActiveSecteur(secteur.id)}
                      className={`inline-flex items-center px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 ${
                        activeSecteur === secteur.id
                          ? 'bg-primary text-primary-foreground shadow-lg'
                          : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
                      }`}
                    >
                      <IconComponent className="h-4 w-4 mr-2" />
                      {secteur.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </ScrollReveal>

          {/* Grille de Circuits */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {circuitsFiltres.map((circuit, index) => (
              <ScrollReveal key={circuit.id} delay={index * 100}>
                <Card className="group border-0 shadow-lg hover:shadow-2xl hover:-translate-y-3 transition-all duration-300 overflow-hidden h-full">
                  <div className="aspect-[4/3] relative overflow-hidden">
                    <img 
                      src={circuit.image} 
                      alt={circuit.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge className={getNiveauColor(circuit.niveau)}>
                        {circuit.niveau}
                      </Badge>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-primary transition-colors">
                        {circuit.title}
                      </h3>
                      
                      <p className="text-slate-600 mb-3 text-sm leading-relaxed">
                        {circuit.description}
                      </p>
                      
                      <p className="text-xs text-slate-500 mb-4 italic">
                        {circuit.ambiance}
                      </p>
                      
                      {/* Infos pratiques */}
                      <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                        <div className="flex items-center gap-1 text-slate-500">
                          <Route className="h-4 w-4" />
                          {circuit.distance}
                        </div>
                        <div className="flex items-center gap-1 text-slate-500">
                          <Clock className="h-4 w-4" />
                          {circuit.duree}
                        </div>
                      </div>
                      
                      {/* Vélo recommandé */}
                      <div className="mb-4">
                        <div className="flex items-center gap-2 text-xs text-slate-600 bg-stone-50 p-3 rounded-lg">
                          <Bike className="h-4 w-4 text-primary" />
                          <span><strong>Vélo conseillé :</strong> {circuit.velo}</span>
                        </div>
                      </div>
                      
                      {/* Points d'intérêt */}
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-slate-700 mb-2">Points d'intérêt :</h4>
                        <div className="flex flex-wrap gap-1">
                          {circuit.highlights.map((highlight, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {highlight}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* CTA */}
                    <div className="mt-auto">
                      <Button asChild size="sm" className="w-full">
                        <Link to="/reserver">
                          Réserver un vélo pour ce circuit
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
          
          {circuitsFiltres.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500 mb-4">Aucun circuit disponible pour ce secteur.</p>
              <Button 
                onClick={() => setActiveSecteur('tous')} 
                variant="outline"
              >
                Voir tous les circuits
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Application GPS Conseillée */}
      <section className="py-12 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-blue-200 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Application GPS conseillée
              </h3>
              <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
                Pour suivre facilement nos circuits, nous recommandons l'utilisation d'une application GPS dédiée aux balades à vélo. 
                Pratique, fiable et spécialisée dans les itinéraires cyclables.
              </p>
              <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white">
                <a href="https://apps.apple.com/fr/app/loopi-balades-gps/id1489707336" target="_blank" rel="noopener noreferrer">
                  Télécharger l'application
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Action Complémentaire - Demande Sur-Mesure */}
      <section className="py-16 bg-gradient-to-r from-primary/5 to-primary/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Vous souhaitez une balade personnalisée ou organisée ?
              </h2>
              <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
                Balade accompagnée, groupe, séminaire, découverte locale… 
                Nous organisons l'expérience vélo parfaite selon vos envies et votre niveau.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="btn-premium">
                  <Link to="/demande-groupe">
                    Faire une demande de balade sur-mesure
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/nous-contacter">
                    Nous contacter pour plus d'infos
                  </Link>
                </Button>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Final - Réservation */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Prêt pour l'aventure ?
            </h2>
            <p className="text-slate-600 mb-8">
              Réservez votre vélo et partez explorer ces magnifiques circuits dès aujourd'hui.
            </p>
            <Button asChild size="lg" className="btn-premium">
              <Link to="/reserver">
                Réserver un vélo maintenant
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </ScrollReveal>
        </div>
      </section>

      <StickyReservationCTA />
      <FloatingFAQ />
      <Footer />
    </div>
  );
};

export default Balades;